/**
 * The destinations of the global quick navigation, built from lists the server has ALREADY
 * limited to what the viewer may open (workspaces, sections, boards, databases, notes, groups) - plus table names from ONE bounded,
 * permission-filtered query that runs only while the viewer types. Nothing
 * here asks the server for a resource by id, so a destination the viewer may not discover cannot
 * appear - and nothing is fetched per keystroke: matching is done on these lists.
 */
export type DestinationKind =
  'workspace' | 'section' | 'group' | 'board' | 'database' | 'table' | 'document';

export interface Destination {
  /** Unique across kinds. */
  key: string;
  kind: DestinationKind;
  name: string;
  /** "Workspace / Section / Group" - where it lives (empty for a workspace). */
  context: string;
  to: string;
  favorite: boolean;
  /** Newest-first rank among recently opened, or -1. */
  recent: number;
}

const KIND_ORDER: Record<DestinationKind, number> = {
  workspace: 0,
  section: 1,
  group: 2,
  board: 3,
  database: 4,
  table: 5,
  document: 6,
};

/** The type chips of the dialog: a section belongs to its workspace, a table to its database. */
export const KIND_FILTERS = ['all', 'workspace', 'group', 'board', 'database', 'document'] as const;
export type KindFilter = (typeof KIND_FILTERS)[number];

const FILTER_OF: Record<DestinationKind, Exclude<KindFilter, 'all'>> = {
  workspace: 'workspace',
  section: 'workspace',
  group: 'group',
  board: 'board',
  database: 'database',
  table: 'database',
  document: 'document',
};

const inKind = (destination: Destination, kind: KindFilter): boolean =>
  kind === 'all' || FILTER_OF[destination.kind] === kind;

/** Whether the viewer is on this destination now (a board's own sub-pages count). */
export function isCurrent(destination: Destination, pathname: string): boolean {
  if (destination.kind === 'section') return false; // a section is a query of its workspace page
  const path = destination.to.split('?')[0] ?? destination.to;
  return pathname === path || pathname.startsWith(`${path}/`);
}

const fold = (text: string) => text.toLocaleLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Matches in this order: name starts with the text, a word of the name starts with it, the name
 * contains it, the place contains it. Every typed word must match somewhere (name or place).
 * Returns `null` for no match, else a rank (lower is better).
 */
export function rank(destination: Destination, query: string): number | null {
  const words = fold(query).split(' ').filter(Boolean);
  if (words.length === 0) return 0;
  const name = fold(destination.name);
  const place = fold(destination.context);
  if (name === words.join(' ')) return -1; // the whole name, typed out: the best answer
  let total = 0;
  for (const word of words) {
    if (name.startsWith(word)) total += 0;
    else if (name.split(/[\s\-_/.]+/).some((part) => part.startsWith(word))) total += 1;
    else if (name.includes(word)) total += 2;
    else if (place.includes(word)) total += 3;
    else return null;
  }
  return total;
}

/** Search results, best first; ties: favorites, recently opened, kind, then name. */
export function search(
  all: readonly Destination[],
  query: string,
  limit = 30,
  kind: KindFilter = 'all',
): Destination[] {
  const scored: { destination: Destination; score: number }[] = [];
  for (const destination of all) {
    if (!inKind(destination, kind)) continue;
    const score = rank(destination, query);
    if (score !== null) scored.push({ destination, score });
  }
  scored.sort(
    (a, b) =>
      a.score - b.score ||
      Number(b.destination.favorite) - Number(a.destination.favorite) ||
      (b.destination.recent >= 0 ? 1 : 0) - (a.destination.recent >= 0 ? 1 : 0) ||
      KIND_ORDER[a.destination.kind] - KIND_ORDER[b.destination.kind] ||
      a.destination.name.localeCompare(b.destination.name),
  );
  return scored.slice(0, limit).map((item) => item.destination);
}

/** With no text: recently opened first (newest first), then favorites. */
export function suggestions(
  source: readonly Destination[],
  limit = 12,
  kind: KindFilter = 'all',
): Destination[] {
  const all = source.filter((d) => inKind(d, kind));
  const recent = all
    .filter((d) => d.recent >= 0)
    .sort((a, b) => a.recent - b.recent)
    .slice(0, limit);
  const seen = new Set(recent.map((d) => d.key));
  const favorites = all
    .filter((d) => d.favorite && !seen.has(d.key))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...recent, ...favorites].slice(0, limit);
}
